import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, ArrowRight, ChevronDown, Phone, Play, ExternalLink } from "lucide-react";
import { services, industries } from "../data/services";
import { serviceRichContent } from "../data/serviceRichContent";
import QuoteForm from "../components/QuoteForm";
import { useState } from "react";

function FAQItem({ faq, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-neutral-800 hover:border-neutral-700 transition-colors">
      <button
        data-testid={`service-faq-${index}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between p-5 text-left gap-4"
      >
        <span className="font-teko text-lg text-white uppercase tracking-wide leading-tight">{faq.q}</span>
        <ChevronDown size={18} className={`text-red-500 flex-shrink-0 mt-0.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-neutral-400 font-inter leading-relaxed">{faq.a}</div>
      )}
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();

  // If no slug, show services overview
  if (!slug) {
    return (
      <>
        <Helmet>
          <title>Custom Screen Printing Services Dallas TX | Ninja Screen Print</title>
          <meta name="description" content="Full-service custom apparel printing in Dallas TX: screen printing, DTF, embroidery, custom jerseys, graphic design & specialty printing. Serving all of DFW." />
        </Helmet>

        <div className="pt-16 bg-[#0d0d0d] border-b border-neutral-800">
          <div className="ninja-container py-16">
            <div className="red-line" />
            <h1 className="font-teko text-5xl sm:text-6xl text-white uppercase tracking-tight">Our Services</h1>
            <p className="text-neutral-400 font-inter mt-3 text-lg max-w-2xl">Full-service custom apparel printing for every need in the DFW Metroplex.</p>
          </div>
        </div>

        <section className="ninja-section">
          <div className="ninja-container">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {services.map((service) => (
                <Link
                  key={service.slug}
                  to={`/services/${service.slug}`}
                  data-testid={`service-overview-${service.slug}`}
                  className="ninja-card p-7 group hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="text-xs text-red-500 uppercase tracking-widest font-inter mb-2">Starting at {service.startingAt}/pc</div>
                  <h2 className="font-teko text-2xl text-white uppercase tracking-wide group-hover:text-red-400 transition-colors mb-2">{service.title}</h2>
                  <p className="text-xs text-neutral-500 font-inter mb-3">{service.subtitle}</p>
                  <p className="text-neutral-400 font-inter text-sm leading-relaxed mb-4">{service.description}</p>
                  <div className="flex items-center gap-2 text-red-500 text-xs font-inter uppercase tracking-widest group-hover:gap-3 transition-all">
                    Learn More <ArrowRight size={12} />
                  </div>
                </Link>
              ))}
            </div>

            {/* Industries */}
            <div className="mt-16">
              <div className="red-line" />
              <h2 className="font-teko text-4xl sm:text-5xl text-white uppercase tracking-tight mb-8">Industries We Serve in Dallas</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {industries.map((ind) => (
                  <div key={ind.slug} data-testid={`industry-card-${ind.slug}`} className="ninja-card p-5">
                    <h3 className="font-teko text-lg text-white uppercase tracking-wide mb-2">{ind.title}</h3>
                    <p className="text-neutral-500 font-inter text-xs leading-relaxed">{ind.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  const service = services.find(s => s.slug === slug);
  const richData = serviceRichContent[slug] || null;

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="font-teko text-6xl text-white uppercase mb-4">Service Not Found</h1>
          <Link to="/services" className="ninja-btn-primary font-teko tracking-widest">VIEW ALL SERVICES</Link>
        </div>
      </div>
    );
  }

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${service.title} Dallas TX`,
    "description": service.description,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Ninja Screen Print",
      "address": { "@type": "PostalAddress", "streetAddress": "Dallas", "addressLocality": "Dallas", "addressRegion": "TX", "postalCode": "75201", "addressCountry": "US" },
      "telephone": "+1-214-555-0190",
      "priceRange": "$$"
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": { "@type": "GeoCoordinates", "latitude": 32.7767, "longitude": -96.7970 },
      "geoRadius": "50 mi"
    },
    "url": `https://ninjascreenprint.com/services/${service.slug}`,
    "image": service.heroImage,
    "offers": {
      "@type": "Offer",
      "priceCurrency": "USD",
      "price": service.startingAt.replace("$", ""),
      "description": `Starting at ${service.startingAt} per piece`,
      "availability": "https://schema.org/InStock"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `${service.title} Options`,
      "itemListElement": service.bestFor.map(item => ({
        "@type": "Offer",
        "itemOffered": { "@type": "Service", "name": item }
      }))
    }
  };

  const articleSchema = richData?.richContent ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${service.title} in Dallas, TX — Complete Guide`,
    "description": service.metaDesc,
    "author": { "@type": "Organization", "name": "Ninja Screen Print", "url": "https://ninjascreenprint.com" },
    "publisher": { "@type": "Organization", "name": "Ninja Screen Print" },
    "mainEntityOfPage": `https://ninjascreenprint.com/services/${service.slug}`,
    "articleSection": service.title,
    "about": { "@type": "Thing", "name": `${service.title} Services` }
  } : null;

  const videoSchema = richData?.videoUrl ? {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": richData.videoCaption || `${service.title} Process Video`,
    "description": `Watch the ${service.title.toLowerCase()} production process at Ninja Screen Print in Dallas, TX.`,
    "contentUrl": richData.videoUrl,
    "thumbnailUrl": service.heroImage,
    "uploadDate": "2025-01-15",
    "publisher": { "@type": "Organization", "name": "Ninja Screen Print" }
  } : null;

  const faqSchema = service.faq?.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": service.faq.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  } : null;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ninjascreenprint.com" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://ninjascreenprint.com/services" },
      { "@type": "ListItem", "position": 3, "name": service.title, "item": `https://ninjascreenprint.com/services/${service.slug}` }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{service.metaTitle}</title>
        <meta name="description" content={service.metaDesc} />
        <meta name="keywords" content={service.keywords?.join(", ")} />
        <link rel="canonical" href={`https://ninjascreenprint.com/services/${service.slug}`} />
        <meta property="og:title" content={service.metaTitle} />
        <meta property="og:description" content={service.metaDesc} />
        <meta property="og:image" content={service.heroImage} />
        <meta property="og:type" content="website" />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {articleSchema && <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>}
        {videoSchema && <script type="application/ld+json">{JSON.stringify(videoSchema)}</script>}
      </Helmet>

      {/* Hero */}
      <div
        className="pt-16 relative min-h-[50vh] flex items-end pb-16 overflow-hidden"
        style={{ backgroundImage: `url('${service.heroImage}')`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
        <div className="relative z-10 ninja-container">
          <nav className="flex items-center gap-2 text-xs text-neutral-500 font-inter mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-white transition-colors">Services</Link>
            <span>/</span>
            <span className="text-red-500">{service.title}</span>
          </nav>
          <div className="text-xs text-red-500 uppercase tracking-widest font-inter mb-2">{service.tagline}</div>
          <h1 className="font-teko text-5xl sm:text-6xl text-white uppercase tracking-tight">{service.title}</h1>
          <p className="text-neutral-300 font-inter mt-3 text-lg max-w-2xl">{service.description}</p>
        </div>
      </div>

      <section className="ninja-section">
        <div className="ninja-container">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-16">
              {/* Key Info Bar */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: "Starting At", value: `${service.startingAt}/pc` },
                  { label: "Min. Quantity", value: `${service.minQuantity} pcs` },
                  { label: "Turnaround", value: service.turnaround },
                ].map(item => (
                  <div key={item.label} className="ninja-card p-4 text-center">
                    <div className="text-xs text-neutral-500 uppercase tracking-widest font-inter mb-1">{item.label}</div>
                    <div className="font-teko text-xl text-red-500 leading-tight">{item.value}</div>
                  </div>
                ))}
              </div>

              {/* Description */}
              <div>
                <div className="red-line" />
                <h2 className="font-teko text-3xl text-white uppercase tracking-tight mb-4">About {service.title} in Dallas</h2>
                <p className="text-neutral-300 font-inter text-sm leading-relaxed">{service.longDescription}</p>
              </div>

              {/* Video Section (if available) */}
              {richData?.videoUrl && (
                <div data-testid="service-video-section">
                  <div className="red-line" />
                  <h2 className="font-teko text-3xl text-white uppercase tracking-tight mb-4">
                    {richData.videoCaption || `Watch: ${service.title} in Action`}
                  </h2>
                  <div className="relative aspect-video bg-neutral-900 border border-neutral-800 overflow-hidden group">
                    <video
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-cover"
                      poster={service.heroImage}
                      data-testid="service-video"
                    >
                      <source src={richData.videoUrl} type="video/mp4" />
                    </video>
                  </div>
                  <p className="text-xs text-neutral-600 font-inter mt-2">
                    Produced at our Dallas, TX production facility. All equipment and processes shown are in-house.
                  </p>
                </div>
              )}

              {/* Rich EEAT Content Sections */}
              {richData?.richContent?.map((section, i) => (
                <article key={i} data-testid={`rich-content-${i}`} className="space-y-6">
                  {/* H2 Heading + Optional H3 Subheading */}
                  <div>
                    <div className="red-line" />
                    <h2 className="font-teko text-3xl sm:text-4xl text-white uppercase tracking-tight leading-none">{section.heading}</h2>
                    {section.subheading && (
                      <h3 className="font-inter text-base text-neutral-400 mt-2">{section.subheading}</h3>
                    )}
                  </div>

                  {/* Section Image */}
                  {section.image && (
                    <div className="relative overflow-hidden rounded-sm border border-neutral-800 aspect-[16/8]">
                      <img
                        src={section.image}
                        alt={section.imageAlt || section.heading}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/40 to-transparent" />
                    </div>
                  )}

                  {/* Paragraphs (short, scannable) */}
                  {section.paragraphs?.map((p, j) => (
                    <p key={j} className="text-neutral-300 font-inter text-sm leading-relaxed max-w-2xl">{p}</p>
                  ))}

                  {/* Highlights (key stats) */}
                  {section.highlights && (
                    <div className="grid grid-cols-3 gap-3">
                      {section.highlights.map((h, j) => (
                        <div key={j} className="bg-neutral-900/60 border border-neutral-800 p-4 text-center">
                          <div className="text-[10px] text-neutral-500 uppercase tracking-widest font-inter mb-1">{h.label}</div>
                          <div className="font-teko text-2xl text-red-500 leading-none">{h.value}</div>
                          <div className="text-[10px] text-neutral-600 font-inter mt-1">{h.detail}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Process Steps */}
                  {section.steps && (
                    <div className="space-y-0">
                      {section.steps.map((step, j) => (
                        <div key={j} className="flex gap-4 py-4 border-b border-neutral-900 last:border-0">
                          <div className="flex-shrink-0 w-8 h-8 bg-red-600 flex items-center justify-center">
                            <span className="font-teko text-white text-lg leading-none">{j + 1}</span>
                          </div>
                          <div>
                            <h3 className="font-teko text-lg text-white uppercase tracking-wide leading-tight">{step.title}</h3>
                            <p className="text-neutral-400 font-inter text-sm mt-0.5">{step.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Comparison Table */}
                  {section.comparison && (
                    <div className="grid grid-cols-2 gap-3">
                      {section.comparison.map((col, j) => (
                        <div key={j} className="bg-neutral-900/40 border border-neutral-800 p-5">
                          <h3 className="font-teko text-lg text-white uppercase tracking-wide mb-3">{col.method}</h3>
                          <ul className="space-y-2">
                            {col.points.map((pt, k) => (
                              <li key={k} className="flex items-start gap-2 text-sm text-neutral-300 font-inter">
                                <CheckCircle size={13} className="text-red-500 shrink-0 mt-0.5" /> {pt}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Checklist */}
                  {section.checklist && (
                    <div className="bg-neutral-900/30 border border-neutral-800 p-5 space-y-2.5">
                      {section.checklist.map((item, j) => (
                        <div key={j} className="flex items-start gap-3 text-sm text-neutral-300 font-inter">
                          <CheckCircle size={14} className="text-red-500 shrink-0 mt-0.5" /> {item}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Additional paragraphs (after comparison/checklist) */}
                  {section.paragraphs2?.map((p, j) => (
                    <p key={`p2-${j}`} className="text-neutral-300 font-inter text-sm leading-relaxed max-w-2xl">{p}</p>
                  ))}

                  {/* Internal Links */}
                  {section.links?.length > 0 && (
                    <div className="flex flex-wrap gap-3 pt-2">
                      {section.links.map((link, j) => (
                        <Link
                          key={j}
                          to={link.to}
                          className="inline-flex items-center gap-2 text-sm text-red-500 hover:text-white bg-red-600/5 hover:bg-red-600/15 border border-red-900/30 hover:border-red-600/50 px-4 py-2 font-inter transition-all group"
                        >
                          <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                          {link.anchor}
                        </Link>
                      ))}
                    </div>
                  )}
                </article>
              ))}

              {/* Features */}
              <div>
                <h2 className="font-teko text-3xl text-white uppercase tracking-tight mb-4">What's Included</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {service.features.map((feat) => (
                    <div key={feat} className="flex items-start gap-2 text-sm text-neutral-300 font-inter py-2 border-b border-neutral-900">
                      <CheckCircle size={14} className="text-red-500 shrink-0 mt-0.5" /> {feat}
                    </div>
                  ))}
                </div>
              </div>

              {/* Best For */}
              <div>
                <h2 className="font-teko text-3xl text-white uppercase tracking-tight mb-4">Best For</h2>
                <div className="flex flex-wrap gap-2">
                  {service.bestFor.map((tag) => (
                    <span key={tag} className="text-sm text-white border border-neutral-700 hover:border-red-600 px-4 py-2 font-inter transition-colors">{tag}</span>
                  ))}
                </div>
              </div>

              {/* Industries We Serve */}
              <div>
                <h2 className="font-teko text-3xl text-white uppercase tracking-tight mb-4">Industries We Serve in Dallas-Fort Worth</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {industries.filter(ind => ind.services.includes(service.title)).map(ind => (
                    <Link key={ind.slug} to={`/industries/${ind.slug}`} className="ninja-card p-3 text-center group">
                      <div className="font-teko text-sm text-white uppercase group-hover:text-red-400 transition-colors">{ind.title}</div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              {service.faq && (
                <div>
                  <h2 className="font-teko text-3xl text-white uppercase tracking-tight mb-4">
                    {service.title} FAQs — Dallas, TX
                  </h2>
                  <div className="space-y-2">
                    {service.faq.map((faq, i) => (
                      <FAQItem key={i} faq={faq} index={i} />
                    ))}
                  </div>
                </div>
              )}

              {/* Related Services */}
              <div>
                <h2 className="font-teko text-2xl text-white uppercase tracking-tight mb-4">Related Services</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {services.filter(s => s.slug !== service.slug).slice(0, 3).map(s => (
                    <Link key={s.slug} to={`/services/${s.slug}`} className="ninja-card p-4 group">
                      <div className="font-teko text-lg text-white uppercase group-hover:text-red-400 transition-colors">{s.title}</div>
                      <div className="text-xs text-neutral-500 font-inter mt-1">from {s.startingAt}/pc</div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Bottom CTA with internal links */}
              <div className="bg-neutral-900/50 border border-neutral-800 p-6">
                <h2 className="font-teko text-2xl text-white uppercase tracking-tight mb-3">Ready to Get Started?</h2>
                <p className="text-neutral-400 font-inter text-sm mb-4">
                  Get a free {service.title.toLowerCase()} quote in under 2 hours. We offer free delivery across the entire DFW metroplex on orders of 24+ pieces.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/contact" className="ninja-btn-primary font-teko tracking-widest px-6 py-3 text-sm">GET A FREE QUOTE</Link>
                  <Link to="/pricing" className="ninja-btn-secondary font-teko tracking-widest px-6 py-3 text-sm">VIEW PRICING</Link>
                  <Link to="/product-catalog" className="ninja-btn-secondary font-teko tracking-widest px-6 py-3 text-sm">BROWSE GARMENTS</Link>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="ninja-card p-6 sticky top-24">
                <div className="red-line" />
                <h3 className="font-teko text-2xl text-white uppercase tracking-wide mb-2">Get a Quote for {service.title}</h3>
                <p className="text-neutral-500 font-inter text-xs mb-4">Free quote in 2 hours. Free DFW delivery on 24+ pieces.</p>
                <QuoteForm compact />
                <div className="mt-4 pt-4 border-t border-neutral-800 flex items-center gap-2">
                  <Phone size={12} className="text-red-500" />
                  <a href="tel:+12145550190" className="text-xs text-neutral-500 hover:text-white transition-colors font-inter">(214) 555-0190</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
